"use client";

import type { ReactNode } from "react";
import { useWatch, type FieldError as HookFieldError, type UseFormReturn } from "react-hook-form";
import type { z } from "zod";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { contactPageFormSchema, ContactPageFormValues } from "@/lib/validation/schemas";

export type ContactFormApi = UseFormReturn<
  z.input<typeof contactPageFormSchema>,
  unknown,
  ContactPageFormValues
>;

function FormField({
  id,
  label,
  required,
  optional,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: HookFieldError;
  children: ReactNode;
}) {
  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id} required={required} optional={optional}>
        {label}
      </FieldLabel>
      {children}
      <FieldError errors={[error]} />
    </Field>
  );
}

function TopicFields({ form }: { form: ContactFormApi }) {
  const inquiryType = useWatch({ control: form.control, name: "inquiryType" });
  if (inquiryType === "order_issue") {
    return (
      <FormField id="contact-orderReference" label="Order or quote number" optional>
        <Input id="contact-orderReference" {...form.register("orderReference")} />
      </FormField>
    );
  }
  if (inquiryType === "sales") {
    return (
      <FormField id="contact-company" label="Company" optional>
        <Input id="contact-company" autoComplete="organization" {...form.register("company")} />
      </FormField>
    );
  }
  return null;
}

export function ContactFormFields({ form }: { form: ContactFormApi }) {
  const { register, formState } = form;
  const { errors } = formState;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <FormField id="contact-name" label="Name" required error={errors.name}>
          <Input id="contact-name" autoComplete="name" aria-invalid={!!errors.name} {...register("name")} />
        </FormField>
      </div>
      <FormField id="contact-email" label="Email" required error={errors.email}>
        <Input id="contact-email" type="email" autoComplete="email" placeholder="you@company.com" aria-invalid={!!errors.email} {...register("email")} />
      </FormField>
      <FormField id="contact-phone" label="Phone" optional error={errors.phone}>
        <Input id="contact-phone" type="tel" autoComplete="tel" placeholder="(555) 123-4567" aria-invalid={!!errors.phone} {...register("phone")} />
      </FormField>
      <TopicFields form={form} />
      <div className="sm:col-span-2">
        <FormField id="contact-message" label="Message" required error={errors.message}>
          <Textarea id="contact-message" rows={5} placeholder="How can we help?" aria-invalid={!!errors.message} {...register("message")} />
        </FormField>
      </div>
    </div>
  );
}
